import { useLocation } from 'react-router-dom'
import { isMobile } from '@/common/utils/utils'
import {
    getProgressWithSubSteps,
    getNextOnboardingStep,
    getPreviousOnboardingStep,
    getCurrentStepNumber,
    getTotalOnboardingSteps,
} from './onboardingFlow'

/**
 * Hook to access onboarding progress information for the current route
 *
 * @param subProgress - Optional sub-progress within current step (0-100). Used for steps with internal screens like KYC.
 *
 * @example
 * ```tsx
 * function OnboardingPage() {
 *   const { progress, currentStep, totalSteps, nextStep } = useOnboardingProgress()
 *
 *   return (
 *     <div>
 *       <ProgressBar progress={progress} />
 *       <p>Step {currentStep} of {totalSteps}</p>
 *     </div>
 *   )
 * }
 * ```
 *
 * @example
 * ```tsx
 * // For steps with sub-progress (like KYC with 4 internal screens)
 * function KycPage() {
 *   const [kycSubProgress, setKycSubProgress] = useState(0)
 *   const { progress } = useOnboardingProgress(kycSubProgress)
 *
 *   return <KycComponent onProgressChange={setKycSubProgress} />
 * }
 * ```
 */
export function useOnboardingProgress(subProgress = 0) {
    const location = useLocation()
    const currentRoute = location.pathname
    const mobile = isMobile()

    return {
        /** Progress percentage (0-100) accounting for sub-steps */
        progress: getProgressWithSubSteps(currentRoute, mobile, subProgress),

        /** Current step number (1-indexed) */
        currentStep: getCurrentStepNumber(currentRoute, mobile),

        /** Total number of steps in the flow */
        totalSteps: getTotalOnboardingSteps(mobile),

        /** Next step route or null if at the end */
        nextStep: getNextOnboardingStep(currentRoute, mobile),

        /** Previous step route or null if at the beginning */
        previousStep: getPreviousOnboardingStep(currentRoute, mobile),

        /** Whether user is on mobile */
        isMobile: mobile,

        /** Current route path */
        currentRoute,
    }
}
